import { api, newIdempotencyKey } from "../../lib/api";
import { BigButton } from "../../design/BigButton";

/*
 * Hint chooser + overlay primitives.
 *
 * Overlays are rendered INSIDE the SceneCanvas transformed stage where the
 * image occupies the full width/height at (0,0)-(100,100). All positioning
 * uses percentages relative to the image so overlays track the image
 * through zoom/pan and any window resize.
 */

const HINT_META = {
  spoken_clue: { icon: "🔊", name: "Spoken Clue" },
  regional_sparkle: { icon: "✨", name: "Regional Sparkle" },
  warm_cold: { icon: "🌡️", name: "Warm & Cold" },
  object_glow: { icon: "💫", name: "Object Glow" },
};

// How long each overlay lingers after activation, in ms.
export const HINT_DURATIONS_MS = {
  object_glow: 3000,
  regional_sparkle: 4000,
  warm_cold: 15000,
};

export function HintChooser({ open, onClose, onPicked, hintConfig, tokenBalance, difficulty, freeHintsRemaining }) {
  if (!open) return null;
  const costs = hintConfig?.costs || {};
  const freeAllow = (hintConfig?.freeHintsByDifficulty || {})[difficulty] || 0;
  // If the caller (GameplayScreen) hasn't given us a specific remaining
  // count yet — because no hint has been used this session — we fall back
  // to the difficulty's max allowance. Easy defaults to 999 (effectively
  // unlimited free hints).
  const remaining = freeHintsRemaining ?? freeAllow;
  const anyFreeLeft = remaining > 0;
  return (
    <div className="hint-chooser" data-testid="hint-chooser" role="dialog" aria-modal>
      <div className="hint-chooser__panel">
        <h2>Choose a hint</h2>
        <div className="hint-chooser__grid">
          {Object.entries(HINT_META).map(([type, meta]) => {
            const cost = costs[type] || 0;
            // A hint is disabled only when the child has NO free hints left
            // AND cannot afford the token cost. This preserves Easy-mode
            // gameplay for kids with low balances.
            const isFree = anyFreeLeft;
            const disabled = !isFree && tokenBalance < cost;
            const badge = isFree ? "Free!" : (cost === 0 ? "Free" : `${cost} tokens`);
            return (
              <button
                key={type}
                className={`hint-chooser__card${isFree ? " hint-chooser__card--free" : ""}`}
                disabled={disabled}
                onClick={() => onPicked(type)}
                data-testid={`hint-choice-${type}`}
                aria-label={`${meta.name}, ${isFree ? "free" : (cost === 0 ? "free" : cost + " tokens")}${disabled ? ", needs more tokens" : ""}`}
              >
                <div style={{ fontSize: 32 }} aria-hidden>{meta.icon}</div>
                <div><b>{meta.name}</b></div>
                <div
                  className={isFree ? "hint-chooser__badge hint-chooser__badge--free" : "hint-chooser__badge"}
                  data-testid={`hint-cost-${type}`}
                >{badge}</div>
              </button>
            );
          })}
        </div>
        <p style={{ color: "#5a4f4a" }} data-testid="hint-chooser-remaining">
          Free hints available: {remaining >= 999 ? "always" : remaining}
        </p>
        <BigButton variant="ghost" onClick={onClose} data-testid="hint-chooser-cancel">
          Cancel
        </BigButton>
      </div>
    </div>
  );
}

/* ----- Scene-embedded overlays (% coords, rendered inside stage) ----- */

export function SparkleOverlay({ region }) {
  if (!region) return null;
  return (
    <div
      className="hint-sparkle"
      style={{
        position: "absolute",
        left: `${region.x}%`,
        top: `${region.y}%`,
        width: `${region.width}%`,
        height: `${region.height}%`,
        pointerEvents: "none",
      }}
      data-testid="regional-sparkle-overlay"
      aria-label="Sparkles indicating hint region"
      role="img"
    >
      <span className="hint-sparkle__particle hint-sparkle__particle--1">✦</span>
      <span className="hint-sparkle__particle hint-sparkle__particle--2">✦</span>
      <span className="hint-sparkle__particle hint-sparkle__particle--3">✦</span>
      <span className="hint-sparkle__particle hint-sparkle__particle--4">✦</span>
      <span className="hint-sparkle__particle hint-sparkle__particle--5">✦</span>
    </div>
  );
}

export function GlowOverlay({ hitbox }) {
  if (!hitbox) return null;
  return (
    <div
      className="hint-glow"
      style={{
        position: "absolute",
        left: `${hitbox.x}%`,
        top: `${hitbox.y}%`,
        width: `${hitbox.width}%`,
        height: `${hitbox.height}%`,
        pointerEvents: "none",
      }}
      data-testid="object-glow-overlay"
      aria-label="Glowing object hint"
      role="img"
    />
  );
}

export function warmthColor(distPct) {
  if (distPct > 40) return { color: "rgba(78,197,241,0.85)", label: "Cold" };
  if (distPct > 20) return { color: "rgba(147,214,255,0.9)", label: "Getting warmer" };
  if (distPct > 8) return { color: "rgba(255,212,71,0.9)", label: "Very warm" };
  return { color: "rgba(255,122,107,0.95)", label: "Hot" };
}

export function WarmColdOverlay({ pointer, targetCenter }) {
  if (!pointer || !targetCenter) return null;
  const dx = pointer.xPercent - targetCenter.xPercent;
  const dy = pointer.yPercent - targetCenter.yPercent;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const w = warmthColor(dist);
  return (
    <div
      className="hint-warm"
      style={{
        position: "absolute",
        left: `${pointer.xPercent}%`,
        top: `${pointer.yPercent}%`,
        transform: "translate(-50%, -50%)",
        backgroundColor: w.color,
        pointerEvents: "none",
      }}
      data-testid="warmcold-indicator"
      aria-label={`Warm-cold hint: ${w.label}`}
      role="status"
    >
      <span className="hint-warm__label">{w.label}</span>
    </div>
  );
}

export async function requestHint({ profileId, levelId, sessionId, levelObjectId, hintType }) {
  const { data } = await api.post("/gameplay/use-hint", {
    childProfileId: profileId,
    levelId,
    levelObjectId,
    sessionId,
    hintType,
    idempotencyKey: newIdempotencyKey(),
  });
  return data;
}
